import React from 'react';
import { X, Eye, Download, Send, ExternalLink, ShieldCheck, Zap } from 'lucide-react';
import { UserProfile } from '../types';
import { formatCurrencyBRL } from '../utils/formatUtils';
import { getTemplateTypeByNiche } from '../utils/nicheUtils';

const PRODUCT_NICHES = [
  'Confeitaria', 'Costura', 'Artesanato', 'Marceneiro', 'Vidraceiro',
  'Transformação',
];

const formatPhone = (phoneStr?: string): string => {
  if (!phoneStr) return '';
  const digits = String(phoneStr).replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return String(phoneStr);
};

const buildFullAddress = (u?: UserProfile): string => {
  if (!u || !u.showAddress || !u.businessAddress) return '';
  
  let addr = u.businessAddress.trim();
  const num = u.businessNumber ? String(u.businessNumber).trim() : '';
  if (num && !addr.includes(num)) {
    if (addr.includes(',')) {
      const commaIdx = addr.indexOf(',');
      addr = `${addr.slice(0, commaIdx)}, Nº ${num}${addr.slice(commaIdx)}`;
    } else {
      addr = `${addr}, Nº ${num}`;
    }
  }
  
  const cityState = [u.city?.trim(), u.state?.trim()].filter(Boolean).join(' - ');
  if (cityState) {
    addr = `${addr} • ${cityState}`;
  }
  return addr;
};

interface PdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile;
  formData: any;
  finalPrice: number | string;
  quoteCount?: number;
  isProUser: boolean;
  onDownloadPDF: () => void;
  onOpenInNewTab?: () => void;
  isGenerating?: boolean;
  generationStep?: string;
  hiddenMarkupTotal?: number;
  useOperationalTax?: boolean;
}

export default function PdfPreviewModal({
  isOpen,
  onClose,
  user,
  formData,
  finalPrice,
  quoteCount = 1,
  isProUser,
  onDownloadPDF,
  onOpenInNewTab,
  isGenerating = false,
  generationStep = '',
  hiddenMarkupTotal = 0,
  useOperationalTax = true,
}: PdfPreviewModalProps) {
  if (!isOpen) return null;

  const todayStr = new Date().toLocaleDateString('pt-BR');
  const products = formData?.products || [];
  const services = formData?.services || [];

  const quoteNiche: string = formData?.category || formData?.categoryName || '';
  const isProductNiche = PRODUCT_NICHES.some(n => quoteNiche.toLowerCase().includes(n.toLowerCase()))
    || (formData?.quoteType === 'product');
  const isCompositeRecipe = (formData?.quoteType === 'manufactured' || (isProductNiche && products.length === 1 && products.some(p => p.isFromRecipe)));
  const isProduct = isCompositeRecipe;
  const isProductOnly = services.length === 0 && products.length > 0;

  const title = user?.businessName || user?.ownerName || 'Prestador de Serviços';
  const phonePart = user?.phone ? `WhatsApp: ${formatPhone(user.phone)}` : '';
  const addressPart = buildFullAddress(user);
  const contactLine = [phonePart, addressPart].filter(Boolean).join(' • ');

  const clientName = formData?.clientName || 'Cliente';
  const serviceName = formData?.serviceName || 'Serviço Profissional';

  const logoSrc = user?.logoUrl || user?.logoBase64;

  const activeTemplateType = getTemplateTypeByNiche(user?.niche || '');
  let footerWarranty = "Este orçamento possui validade de 15 dias após a data de emissão.";
  if (user?.niche) {
    if (activeTemplateType === 'FOOD') footerWarranty = "Validade recomendada para consumo: 7 dias.";
    else if (activeTemplateType === 'TECHNICAL') footerWarranty = "Garantia legal de 90 dias para os serviços prestados, conforme CDC.";
  }

  const rawPixKey = (user?.pixKey || formData?.pixKey || '').toString().trim();
  const isPixVisible = Boolean((user?.showPixKey ?? formData?.showPixKey ?? true) && rawPixKey.length > 0);
  const rawPixType = (user?.pixKeyType || formData?.pixKeyType || 'PIX').toString().trim().toUpperCase();

  const totalProductsCost = products.reduce((acc: number, p: any) => acc + (Number(p.total) || 0), 0);
  const totalServicesCost = services.reduce((acc: number, s: any) => acc + (Number(s.value) || 0), 0);
  const currentSum = totalProductsCost + totalServicesCost;
  const globalMultiplier = currentSum > 0 ? Number(finalPrice) / currentSum : 1;

  const itemsDisplay = products.map((p: any) => {
    let displayValue = 0;
    if (p.isPassThrough || p.isMarkupHidden) {
      displayValue = p.total || 0;
    } else {
      displayValue = (p.total || 0) * globalMultiplier;
    }
    return { ...p, displayValue };
  });

  const itemsTotalDisplayed = itemsDisplay.reduce((acc: number, p: any) => acc + p.displayValue, 0);
  const servicesTotalDisplayed = services.reduce((acc: number, s: any) => acc + (Number(s.value) || 0), 0);
  const expectedLaborDisplay = useOperationalTax 
    ? 0 
    : Math.max(0, Number(finalPrice) - itemsTotalDisplayed - servicesTotalDisplayed - (useOperationalTax ? hiddenMarkupTotal : 0));

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-zinc-950/80 backdrop-blur-md p-2 sm:p-4 overflow-y-auto font-sans">
      <div className="relative w-full max-w-4xl bg-zinc-900 border border-zinc-800 rounded-3xl shadow-2xl flex flex-col my-auto max-h-[92vh] overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800 bg-zinc-900/90 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand">
              <Eye className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-black text-white uppercase tracking-wider">
                  Prévia do Orçamento
                </h2>
                {isProUser ? (
                  <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-black uppercase px-2 py-0.5 rounded-md flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Pro • White-Label
                  </span>
                ) : (
                  <span className="bg-zinc-800 text-zinc-400 border border-zinc-700 text-[10px] font-bold uppercase px-2 py-0.5 rounded-md">
                    Versão Grátis
                  </span>
                )}
              </div>
              <p className="text-[11px] text-zinc-400">
                Visualização exata do documento que o seu cliente receberá.
              </p>
            </div>
          </div>
          
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-colors cursor-pointer"
            title="Fechar Prévia"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content - Scrollable PDF Sheet */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-zinc-950/40">
          <div className="max-w-2xl mx-auto bg-white text-zinc-900 rounded-2xl shadow-xl p-6 sm:p-10 border border-zinc-200 relative">
            
            {/* Header: Logo + Business Info */}
            <div className="flex justify-between items-start border-b border-zinc-200 pb-6 mb-6">
              <div className="text-left space-y-1.5 max-w-[65%]">
                {logoSrc ? (
                  <img 
                    src={logoSrc} 
                    alt="Logo" 
                    className="w-14 h-14 rounded-full object-cover border border-zinc-200 mb-2" 
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-900 font-black text-xs mb-2">
                    R MAX
                  </div>
                )}
                <h3 className="text-lg sm:text-xl font-black text-zinc-950 uppercase tracking-tight leading-tight">
                  {title}
                </h3>
                {contactLine && (
                  <p className="text-xs text-zinc-500 font-medium leading-relaxed">
                    {contactLine}
                  </p>
                )}
              </div>

              <div className="text-right space-y-2 shrink-0">
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">NÚMERO</p>
                  <p className="text-base sm:text-lg font-black text-zinc-950">{(quoteCount + 1).toString().padStart(4, '0')}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">DATA</p>
                  <p className="text-xs sm:text-sm font-black text-zinc-800">{todayStr}</p>
                </div>
              </div>
            </div>

            {/* Recipient */}
            <div className="mb-6 text-left">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-0.5">PROPOSTA PARA</p>
              <p className="text-xl sm:text-2xl font-black text-zinc-950">{clientName}</p>
            </div>

            {/* Table */}
            <div className="mb-6 overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-zinc-950">
                    <th className="py-2.5 text-xs font-black text-zinc-500 uppercase tracking-wider">
                      {isProduct ? 'PRODUTO' : isProductOnly ? 'PRODUTO / ITEM' : 'DESCRIÇÃO DO SERVIÇO'}
                    </th>
                    <th className="py-2.5 text-xs font-black text-zinc-500 uppercase tracking-wider text-right">
                      TOTAL
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {isProduct ? (
                    <tr>
                      <td className="py-4 text-left">
                        <div className="text-base font-bold text-zinc-950">{serviceName}</div>
                        {products.length > 0 && (
                          <div className="mt-2 pl-3 border-l-2 border-zinc-200 space-y-1">
                            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Componentes inclusos:</p>
                            {products.filter((p: any) => p.showOnPdf !== false).map((m: any, idx: number) => (
                              <p key={idx} className="text-xs text-zinc-600">
                                • {m.quantity}x {m.name}
                              </p>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-4 text-right align-top font-black text-zinc-950 text-base">
                        {formatCurrencyBRL(Number(finalPrice) || 0)}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {itemsDisplay.map((p: any, idx: number) => (
                        <tr key={`prod_${idx}`}>
                          <td className="py-3 text-left">
                            <div className="text-sm font-bold text-zinc-900">{p.name || 'Produto / Item'}</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">
                              {p.quantity || 1}x {formatCurrencyBRL(p.unitValue || 0)} {!isProductOnly ? (!p.isMarkupHidden && !p.isPassThrough ? '(Insumo Comercializado)' : '(Custo Base)') : ''}
                            </div>
                          </td>
                          <td className="py-3 text-right font-bold text-zinc-900 text-sm">
                            {formatCurrencyBRL(p.displayValue)}
                          </td>
                        </tr>
                      ))}

                      {services.map((s: any, idx: number) => {
                        const isAlreadyMentioned = (s.name || '').toLowerCase().includes('por item') || (s.name || '').toLowerCase().includes(`por ${s.unitLabel || 'item'}`);
                        const unitSuffix = s.isUnitBased && !isAlreadyMentioned ? `(por ${s.unitLabel || 'item'})` : '';

                        return (
                          <tr key={`srv_${idx}`}>
                            <td className="py-3 text-left">
                              <div className="text-sm font-bold text-zinc-900">{s.name || 'Serviço Profissional'}</div>
                              {(s.isUnitBased || (s.quantity && s.quantity > 1) || (s.unitValue && s.unitValue > 0 && s.unitValue !== s.value)) && (
                                <div className="text-[11px] text-zinc-500 mt-0.5">
                                  {s.quantity || 1}x {formatCurrencyBRL(s.unitValue || s.value)} {unitSuffix}
                                </div>
                              )}
                            </td>
                            <td className="py-3 text-right font-bold text-zinc-900 text-sm">
                              {formatCurrencyBRL(Number(s.value) || 0)}
                            </td>
                          </tr>
                        );
                      })}

                      {useOperationalTax && hiddenMarkupTotal > 0 && (
                        <tr>
                          <td className="py-3 text-left">
                            <div className="text-sm font-bold text-zinc-900">Custos Operacionais e Logística</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">Gestão, Margem de Segurança e Reserva</div>
                          </td>
                          <td className="py-3 text-right font-bold text-zinc-900 text-sm">
                            {formatCurrencyBRL(hiddenMarkupTotal)}
                          </td>
                        </tr>
                      )}

                      {(expectedLaborDisplay > 0.50 || (itemsDisplay.length === 0 && services.length === 0 && !useOperationalTax)) && (
                        <tr>
                          <td className="py-3 text-left">
                            <div className="text-sm font-bold text-zinc-900">
                              {formData?.serviceDescription?.trim() || 'Mão de Obra / Execução do Serviço'}
                            </div>
                          </td>
                          <td className="py-3 text-right font-bold text-zinc-900 text-sm">
                            {formatCurrencyBRL(expectedLaborDisplay > 0.50 ? expectedLaborDisplay : Number(finalPrice))}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Investment */}
            <div className="border-t-2 border-zinc-200 py-4 text-right">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">
                VALOR TOTAL DO INVESTIMENTO
              </p>
              <h2 className="text-2xl sm:text-3xl font-black text-zinc-950 tracking-tight">
                {formatCurrencyBRL(Number(finalPrice) || 0)}
              </h2>
            </div>

            {/* Terms & PIX */}
            <div className="border-t border-zinc-100 pt-4 space-y-2 text-left">
              <p className="text-xs font-bold text-zinc-700">{footerWarranty}</p>
              <p className="text-xs text-zinc-500">Prazo: {formData?.deadline || 'A combinar'}</p>

              {isPixVisible && (
                <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 rounded-xl">
                  <p className="text-[10px] font-black text-zinc-500 uppercase tracking-wider mb-0.5">
                    Dados para Pagamento via PIX:
                  </p>
                  <p className="text-xs sm:text-sm font-black text-zinc-900">
                    Chave {rawPixType}: <span className="text-blue-700">{rawPixKey}</span>
                  </p>
                </div>
              )}

              <p className="text-[11px] text-zinc-400 pt-2 border-t border-zinc-100">
                Assinado por: <strong className="text-zinc-700">{user?.ownerName || 'Profissional'}</strong>
              </p>
            </div>

            {/* Footer Branding for Free Tier */}
            {!isProUser ? (
              <div className="mt-6 p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg text-center">
                <p className="text-[11px] text-zinc-600 font-bold">
                  ⚡ <strong className="text-blue-900">R-MAX PRECIFICAÇÃO</strong> • Orçamento gerado com precisão matemática.
                </p>
              </div>
            ) : (
              <div className="mt-4 pt-2 text-center">
                <span className="text-[9px] text-zinc-400 uppercase tracking-widest font-bold">
                  ✓ Documento Oficial Personalizado com sua Marca
                </span>
              </div>
            )}

          </div>
        </div>

        {/* Modal Actions Footer */}
        <div className="p-4 sm:p-5 border-t border-zinc-800 bg-zinc-900 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-zinc-400 text-center sm:text-left">
            {isProUser ? (
              <span className="text-emerald-400 font-bold">✓ PDF oficial personalizado com sua marca</span>
            ) : (
              <span>Plano Grátis inclui marca d'água</span>
            )}
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            {onOpenInNewTab && (
              <button
                type="button"
                onClick={onOpenInNewTab}
                disabled={isGenerating}
                className="flex-1 sm:flex-none py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-all cursor-pointer border border-zinc-700 active:scale-95"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Visualizar PDF</span>
              </button>
            )}

            <button
              type="button"
              onClick={onDownloadPDF}
              disabled={isGenerating}
              className="flex-1 sm:flex-none py-3 px-5 bg-brand hover:bg-brand-dark text-white font-black rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-lg shadow-brand/20 cursor-pointer active:scale-95"
            >
              <Download className="h-4 w-4" />
              <span>{isGenerating ? (generationStep || 'Gerando...') : 'Baixar / Compartilhar PDF'}</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
